// conventions-allow: file-size — this component owns the established table orchestration seams.
import type { ReactNode } from 'react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { colors } from 'styles/variables'
import LoadingPlaceholderContainer from 'ui/atoms/LoadingPlaceholderContainer/LoadingPlaceholderContainer'
import { HourlyDataTable } from 'ui/components/HourlyDataTable/HourlyDataTable'
import { getCellDisplay } from 'ui/components/HourlyDataTable/HourlyDataTable.cellEdit.helper'
import hourlyStyles from 'ui/components/HourlyDataTable/HourlyDataTable.module.less'
import type { TableCellEditRule } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { HourlyInputCell } from 'ui/components/HourlyDataTable/HourlyInputCell'
import { useTableCellClipboard } from 'ui/components/HourlyDataTable/hooks/useTableCellClipboard'
import { useTableCellEdit } from 'ui/components/HourlyDataTable/hooks/useTableCellEdit'
import { useTableCellNavigation } from 'ui/components/HourlyDataTable/hooks/useTableCellNavigation'
import { useTableCellSelection } from 'ui/components/HourlyDataTable/hooks/useTableCellSelection'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { useDayAheadBidTagSelectionContext } from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/useDayAheadBidTagSelectionContext'
import { getDayAheadBidBuySellGroups, getDayAheadBidCellKey } from './DayAheadBidTable.grid.helper'
import type {
  DayAheadBidColumn,
  DayAheadBidProductionConsumption,
  DayAheadBidTableResetController,
} from './DayAheadBidTable.types'
import { useDayAheadBidTableEdits } from './useDayAheadBidTableEdits'
import { useDayAheadBidTableModel } from './useDayAheadBidTableModel'
import { useDayAheadBidTableSaveBehavior } from './useDayAheadBidTableSave'
import { usePendingBidEditsGuard } from './usePendingBidEditsGuard'

const NAME_ROW_RULE: TableCellEditRule = { kind: 'text' }
const BID_VALUE_RULE: TableCellEditRule = { kind: 'number', admitsSign: true }

type DayAheadBidTableProps = {
  partitionId: number | undefined
  productionConsumption: DayAheadBidProductionConsumption
  tableContextKey: string
  tableResetController: DayAheadBidTableResetController
}

function getBuySellHeaderStyle(buySell: DayAheadBidColumn['buySell']): {
  backgroundColor: string
  borderBottom: string
} {
  return {
    backgroundColor: buySell === 'buy' ? colors.success1 : colors.danger1,
    borderBottom: `1px solid ${buySell === 'buy' ? colors.success2 : colors.danger2}`,
  }
}

export function DayAheadBidTable({
  partitionId,
  productionConsumption,
  tableContextKey,
  tableResetController,
}: DayAheadBidTableProps): ReactNode {
  const { t } = useTranslation()
  const elplanSnap = useSnapshot(elplanStore)
  const { tagsForDate, tagsStatus, requireTagForBlockBids, blockBidInputCurrency } = useDayAheadBidTagSelectionContext({
    partitionId,
  })
  const fallbackDay = Datetime.getTomorrowDate()
  const startTime = elplanSnap.date.start_time ?? fallbackDay.startTime
  const endTime = elplanSnap.date.end_time ?? fallbackDay.endTime
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const {
    renderedColumns,
    configuredColumns,
    rows,
    rowLabels,
    adapter,
    valuesByRow,
    skipped,
    isBidsPending,
    isBidsError,
    configuredTagsStatus,
    isReady,
    saveContextKey,
  } = useDayAheadBidTableModel({
    elplanInfoId: elplanSnap.elplan_info_id,
    startTime,
    endTime,
    partitionId,
    productionConsumption,
    tagsForDate,
    tagsStatus,
    requireTagForBlockBids,
    blockBidInputCurrency,
  })
  const textRowIndex = rows.findIndex((row) => row.kind === 'name')
  const { values, setValues, valuesRef, isDirty, isCellUnsaved, revert, commit } = useDayAheadBidTableEdits(
    valuesByRow,
    textRowIndex,
    tableResetController,
    tableContextKey
  )
  const { handleSave, isPending, bannerMessage, faultMessageByCellKey } = useDayAheadBidTableSaveBehavior({
    values,
    configuredColumns,
    renderedColumns,
    rows,
    rowLabels,
    elplanInfoId: elplanSnap.elplan_info_id,
    startTime,
    partitionId,
    isReady,
    isDirty,
    saveContextKey,
    commit,
    t,
  })
  usePendingBidEditsGuard(isDirty, revert, isReady ? handleSave : null, isPending)
  const inputRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({})
  const {
    selectedCellKeys,
    selectedCellKeysRef,
    selectedCellKeysForClipboardRef,
    anchorCellKeyForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets,
    takeReplaceOnType,
  } = useTableCellSelection({ adapter, tableContainerRef })

  function getCellRule(cellKey: string): TableCellEditRule {
    const rowIndex = adapter.getCoordinate(cellKey)?.rowIndex
    return rowIndex !== undefined && rows[rowIndex]?.kind === 'name' ? NAME_ROW_RULE : BID_VALUE_RULE
  }

  function clearValuesForSelection(cellKeys: Set<string>): void {
    setValues((previous) => {
      const next = previous.map((row) => ({ ...row }))
      cellKeys.forEach((cellKey) => {
        const coordinate = adapter.getCoordinate(cellKey)
        if (coordinate) {
          next[coordinate.rowIndex] = { ...(next[coordinate.rowIndex] ?? {}), [cellKey]: '' }
        }
      })
      return next
    })
  }

  const { handleCellKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
    adapter,
    clearValuesForSelection,
    clearSelection,
    replaceSelection,
    setAnchorCellKey,
    inputRefs,
    bulkReplaceOnNextInputRef,
    selectedCellKeysRef,
  })

  const { handleCellValueChange, handleCellKeyDown } = useTableCellEdit({
    adapter,
    inputRefs,
    valuesByRow: values,
    setValuesByRow: setValues,
    getCellRule,
    getEditTargets,
    takeReplaceOnType,
    handleNavigationKeyDown,
  })

  useTableCellClipboard({
    tableContainerRef,
    adapter,
    anchorCellKeyForClipboardRef,
    selectedCellKeysForClipboardRef,
    valuesByRowRef: valuesRef,
    setValuesByRow: setValues,
  })

  if (partitionId === undefined) {
    return <InfoBanner text={t('Select a partition to edit day-ahead bids')} />
  }

  if (tagsStatus === 'pending' || configuredTagsStatus === 'pending' || isBidsPending) {
    return (
      <div role="status" aria-label={t('Loading')}>
        <LoadingPlaceholderContainer height={400} />
      </div>
    )
  }

  if (tagsStatus === 'error' || configuredTagsStatus === 'error' || isBidsError) {
    return <InfoBanner style="danger" text={t('The bids for this date could not be loaded')} />
  }

  if (!isReady) {
    return <InfoBanner style="danger" text={t('The bids for this date could not be reconciled safely')} />
  }

  const { groups: buySellGroups, columns: groupedColumns } = getDayAheadBidBuySellGroups(renderedColumns)

  return (
    <div className="flex flex-col gap-3">
      {bannerMessage !== undefined && <InfoBanner style="danger" text={bannerMessage} />}
      {skipped.length > 0 && (
        <InfoBanner
          style="warning"
          text={t('Some bids for this date are not shown in the table: {{bidNames}}', {
            bidNames: skipped.map(({ name }) => name).join(', '),
          })}
        />
      )}
      {renderedColumns.length === 0 ? (
        <InfoBanner text={t('No bid tags are configured for this date')} />
      ) : (
        <HourlyDataTable
          rowCount={rows.length}
          tableContainerRef={tableContainerRef}
          getRowLabel={(rowIndex) => rowLabels[rowIndex] ?? ''}
          columnHeaders={buySellGroups.map((group) => (
            <th
              key={group.buySell}
              className={hourlyStyles.HourlyDataTable_BidTableGroupColumnHeader}
              style={getBuySellHeaderStyle(group.buySell)}
              colSpan={group.columns.length}
            >
              {group.buySell === 'buy' ? t('Buy') : t('Sell')}
            </th>
          ))}
          subColumnHeaders={groupedColumns.map(({ column, isGroupStart, isGroupEnd }) => {
            return (
              <th
                key={column.tagId}
                className={[
                  hourlyStyles.HourlyDataTable_BidTableThSub,
                  isGroupStart ? hourlyStyles.HourlyDataTable_BidTableGroupStartCell : '',
                  isGroupEnd
                    ? hourlyStyles.HourlyDataTable_BidTableGroupEndCell
                    : hourlyStyles.HourlyDataTable_BidTableThSubNoSeparator,
                  hourlyStyles.HourlyDataTable_BidTableStickyTopSecondary,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={getBuySellHeaderStyle(column.buySell)}
              >
                {column.tagKey}
              </th>
            )
          })}
          isFullHeight
          getRowClassName={(rowIndex) =>
            rows[rowIndex]?.kind === 'price' || rows[rowIndex]?.kind === 'name'
              ? hourlyStyles.HourlyDataTable_BidTableEmphasizedRow
              : ''
          }
          renderCellsForRow={(rowIndex) =>
            groupedColumns.map(({ column, isGroupStart, isGroupEnd }) => {
              const cellKey = getDayAheadBidCellKey(rowIndex, column.tagId)
              const isPriceRow = rows[rowIndex]?.kind === 'price'
              const isNameRow = rows[rowIndex]?.kind === 'name'
              const faultMessage = faultMessageByCellKey.get(cellKey)

              return (
                <HourlyInputCell
                  key={cellKey}
                  cellKey={cellKey}
                  value={getCellDisplay(values[rowIndex]?.[cellKey] ?? '', getCellRule(cellKey))}
                  isSelected={selectedCellKeys.has(cellKey)}
                  isInvalid={faultMessage !== undefined}
                  faultMessage={faultMessage}
                  isGroupStart={isGroupStart}
                  isGroupEnd={isGroupEnd}
                  ariaLabel={
                    isPriceRow
                      ? t('Average price for {{tagKey}}', { tagKey: column.tagKey })
                      : isNameRow
                        ? t('Name for {{tagKey}}', { tagKey: column.tagKey })
                        : t('Volume for {{tagKey}}, {{hour}}', {
                            tagKey: column.tagKey,
                            hour: rowLabels[rowIndex],
                          })
                  }
                  isUnsaved={isCellUnsaved(rowIndex, cellKey)}
                  onValueChange={(value) => handleCellValueChange(cellKey, value)}
                  onFocus={() => {}}
                  onBlur={() => {}}
                  onKeyDown={(event) => handleCellKeyDown(cellKey, event)}
                  onMouseDown={(ev) => handleCellMouseDown(cellKey, ev)}
                  onMouseOver={() => handleCellMouseOver(cellKey)}
                  multiline={isNameRow}
                  registerRef={(element: HTMLInputElement | HTMLTextAreaElement | null) => {
                    inputRefs.current[cellKey] = element
                  }}
                />
              )
            })
          }
        />
      )}
    </div>
  )
}
